import { connect } from 'react-redux';
import { useState, useEffect, useRef, Fragment  } from 'react';

import { setLoading, setAlert, siteboardAction } from '../actions/defaultActions';
import {
  Email, SiteboardApiValidator, UserName, GET_SCHEDULE_CONTACT_UNSUBSCRIBE_LINK_DETAILS, STOP_USING_UNSUBSCRIBE_SCHEDULE_CONTACT_LINK, INVITE_LINK_EXPIRE_TIME_DAYS, 
  REDEEM_SCHEDULE_CONTACT_UNSUBSCRIBE_LINK, REQUEST_FRESH_SCHEDULE_CONTACT_UNSUBSCRIBE_LINK,
} from '../actions/types';
import { getServerApiResponse } from '../Util';

import PoweredByBar from './PoweredByBar';
import ParallaxSection from './ParallaxSection';
import Tooltip from './Tooltip';
import PushButton from './PushButton';

import Logo from '../SiteBoard_Logo';
import BackIcon from '../icons/backIcon';
import InfoIcon from '../icons/infoIcon';
import CircleCheckIcon from '../icons/circleCheckIcon';

const testMode = false;

const SubscriptionContactUnsubscribePage = ({_unsubscribeCode, _unsubscribeEmail, setAlert, setLoading, siteboardAction}) => {
  const disabledInputStyle = 'w-full text-white rounded-md px-3 py-1.5 bg-[#444247] focus:bg-[#656565]';

  const [inputEmail, setInputEmail] = useState(_unsubscribeEmail);
  const [linkInfoLoading, setLinkInfoLoading] = useState(true);
  const [projectNames, setProjectNames] = useState([]);
  const [isExpired, setIsExpired] = useState(false);
  const [alreadyUnsubscribed, setAlreadyUnsubscribed] = useState(false);
  const [newUnsubscribeLinkSet, setNewUnsubscribeLinkSet] = useState(false);
  const [successRedeemingLink, setSuccessRedeemingLink] = useState(false);

  const fetchInfoState = useRef('none');

  useEffect(() => {
    if(testMode) {
      setInputEmail('myemail@stuff.com');
      setIsExpired(false);
      setAlreadyUnsubscribed(false);
      setProjectNames(['Ricks Warehouse', 'Two', 'Three', 'Four', 'Five']);
      setLinkInfoLoading(false);
      setSuccessRedeemingLink(false);
      return;
    }

    if(fetchInfoState.current !== 'none') return;

    const onApiSuccess = (data) => {
      try {
        fetchInfoState.current = 'complete';
        const validOutput = SiteboardApiValidator[GET_SCHEDULE_CONTACT_UNSUBSCRIBE_LINK_DETAILS].validateOutput(data); 
        setIsExpired(validOutput.isExpired);
        setAlreadyUnsubscribed(validOutput.alreadyUnsubscribed);
        setProjectNames(validOutput.projectNames);
        setLinkInfoLoading(false);
      } catch(error) {
        onApiError(error.message);
      }
    };
    const onApiError = (errorMessage) => {
      fetchInfoState.current = 'none';
      siteboardAction(STOP_USING_UNSUBSCRIBE_SCHEDULE_CONTACT_LINK, {});
      window.history.pushState('', '', '?'); // clear the url
      setAlert(typeof errorMessage === 'string' ? errorMessage : 'error getting unsubscribe info');
    };
    getServerApiResponse({ 
      action: GET_SCHEDULE_CONTACT_UNSUBSCRIBE_LINK_DETAILS,
      unsubscribeEmail: _unsubscribeEmail,
      unsubscribeCode: _unsubscribeCode,
    }, onApiSuccess, onApiError);

    fetchInfoState.current = 'pending';
  }, [_unsubscribeCode, _unsubscribeEmail, setAlert, siteboardAction]);

  const redeemUnsubscribeLink = () => {
    let apiArgs = null;
    try {
      apiArgs = SiteboardApiValidator[REDEEM_SCHEDULE_CONTACT_UNSUBSCRIBE_LINK].validateInput({
        action: REDEEM_SCHEDULE_CONTACT_UNSUBSCRIBE_LINK,
        unsubscribeEmail: _unsubscribeEmail,
        unsubscribeCode: _unsubscribeCode,
      });
    } catch(error) {
      setAlert(error.message);
      return;
    }

    const onApiSuccess = (data) => {
      setLoading('');
      try {
        const validOutput = SiteboardApiValidator[REDEEM_SCHEDULE_CONTACT_UNSUBSCRIBE_LINK].validateOutput(data); 
        setSuccessRedeemingLink(true);
        //siteboardAction(STOP_USING_UNSUBSCRIBE_SCHEDULE_CONTACT_LINK, {});
        //window.history.pushState('', '', '?'); // clear the url
      } catch(error) {
        onApiError(error.message);
      }
    };
    const onApiError = (errorMessage) => {
      setLoading('');
      setAlert(typeof errorMessage === 'string' ? errorMessage : 'There was an error unsubscribing your email');
    };
    setLoading('Unsubscribing Your Email');
    getServerApiResponse(apiArgs, onApiSuccess, onApiError);
  };

  const requestNewUnsubscribeLink = () => {
    const onApiSuccess = (data) => {
      setLoading('');
      setNewUnsubscribeLinkSet(true);
    };
    const onApiError = (errorMessage) => {
      setLoading('');
      setAlert(typeof errorMessage === 'string' ? errorMessage : 'error getting unsubscribe link');
    };
    setLoading('Sending Unsubscribe Link');
    getServerApiResponse({ 
      action: REQUEST_FRESH_SCHEDULE_CONTACT_UNSUBSCRIBE_LINK,
      unsubscribeEmail: _unsubscribeEmail,
      unsubscribeCode: _unsubscribeCode,
    }, onApiSuccess, onApiError);
  };

  const getProjectNamesJSX = () => {
    if(projectNames.length === 0) return <p className='inline'>no projects</p>;
    return (
      <>
        {projectNames.map((projectName, index) => (
          <Fragment key={index}>
            {(index === (projectNames.length-1) && projectNames.length > 1) && <p className='inline'> and </p> }
            {(index > 0 && index < (projectNames.length-1) && projectNames.length > 1) && <p className='inline'>, </p> }
            <p className='inline'>"{projectName}"</p>
          </Fragment>
        ))}
      </>
    );
  };

  const getUnsubscribeJSX = () => {
    if(linkInfoLoading) return <></>;

    const footerInfo = (
      <div className='flex flex-col gap-2'>
        <div className='w-full h-[1px] bg-white my-4'></div>
        <div className='italic'>
          {process.env.REACT_APP_WEBSITE_NAME} lets you quickly create and update <span className='text-nowrap'>3-week</span> lookahead schedules without spreadsheets.
        </div>
        <div className='italic'>
          If you need assistance, contact us at 
          <a className='ml-1 text-[#f9a928]' href={`mailto:${process.env.REACT_APP_SUPPORT_EMAIL}`}>{process.env.REACT_APP_SUPPORT_EMAIL}</a>
        </div>
      </div>
    );

    if(alreadyUnsubscribed) {
      return (
        <div className='flex flex-col gap-4'>
          <div className='flex items-start gap-2 text-[1.2em] pt-4'>
            <CircleCheckIcon size='1.4em' color='#00bc7d' />
            <div>All set</div>
          </div>
          <div>You've already opted out of schedule update emails from this sender.</div>
          {footerInfo}
        </div>
      );
    }

    if(isExpired) {
      return (
        <>
          {newUnsubscribeLinkSet === false ? 
            <>
              <div>Your unsubscribe link is more than {INVITE_LINK_EXPIRE_TIME_DAYS} days old and has expired for security reasons. To proceed, we can send a new unsubscribe link to <b>{inputEmail}</b>.</div>
              <div className='flex flex-col gap-4 bg-[#44444444] rounded-lg p-4'>
                <div className='font-medium'>Click the button below to receive a fresh unsubscribe link via email.</div>
                <div className='flex flex-col gap-y-6'>
                  {/* submit email button */}
                  <PushButton 
                    textColor='black'
                    bgColor='#f9a928'
                    hoverBgColor='#f79800'
                    onClick={() => { requestNewUnsubscribeLink(); }}
                  >
                    Send New Unsubscribe Link
                  </PushButton>
                </div>
              </div>
            </>
            :
            <div>
              We sent a new unsubscribe link to <b>{_unsubscribeEmail}</b>. Please check your inbox and use the new link. If you don’t see the email shortly, be sure to check your spam or junk folder.
            </div>
          }
          {footerInfo}
        </>
      );
    }

    if(successRedeemingLink) {
      return (
        <div className='flex flex-col gap-4'>
          <div className='flex items-start gap-2 text-[1.2em] pt-4'>
            <CircleCheckIcon size='1.4em' color='#00bc7d' />
            <div>All set</div>
          </div>
          <div>You won't receive any more schedule update emails for {getProjectNamesJSX()}.</div>
          {footerInfo}
        </div>
      );
    }

    return (
      <>
        <div className='flex flex-col gap-2 max-w-[90vw] overflow-x-auto'>
          <div className="text-white mt-2">
            <p className="font-bold mb-2 text-[1.2em]">
              Confirm you want to stop receiving schedule updates for the following projects:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              {projectNames.map((name, index) => <li key={index}>{name}</li>)}
            </ul>
          </div>
        </div>
        <div className='flex flex-col gap-4 bg-[#44444466] rounded-lg p-4'>
          {/* Email Input */}
          <div>
            <label className='block text-sm font-medium mb-1'>Email</label>
            <div className='rounded-[12px] border border-[#0000003f] h-[32px] '>
              <input 
                value={inputEmail}
                className={disabledInputStyle}
                type='text' 
                disabled={true}
                onChange={(event) => {}}
              />
            </div>
          </div>
          <div className='flex flex-col gap-y-6'>
            {/* submit email button */}
            <PushButton 
              textColor='black'
              bgColor='#f9a928'
              hoverBgColor='#f79800'
              onClick={() => { redeemUnsubscribeLink(); }}
            >
              Unsubscribe
            </PushButton>
          </div>
        </div>
        {footerInfo}
      </>
    );
  };
  
  return (
    <div className={`relative flex flex-col justify-between min-h-screen bg-[#151515] text-white overflow-hidden`}>
      <div className='relative z-[1] grow flex flex-col justify-center items-center max-w-screen-sm px-2 py-6 text-[1.2em] w-full mx-auto'>
        <div className='grow flex flex-col justify-center items-center'>
          <div className='flex flex-col gap-6'>
            {/* SiteBoard logo */}
            <div>
              <a href='/' className={'flex items-center gap-x-2 font-bold text-[1.8em]'}>
                <div className={`${linkInfoLoading ? 'animate-[spin_1s_linear_infinite]' : ''}`}>
                  <Logo size={'1em'} />
                </div>
                <div className='flex flex-col'>
                {process.env.REACT_APP_WEBSITE_NAME} 
                </div>
              </a>
              {linkInfoLoading === true && 
                <div className='font-medium text-[#aaa] text-[1.2em]'>Retrieving Your Unsubscribe Link</div>
              }
            </div>
            {getUnsubscribeJSX()}
          </div>
        </div>
      </div>
      <div className='z-[1]'>
        <PoweredByBar />
      </div>
      {/* construction site background */}
      <div className='z-[0] absolute top-[0vh] left-[0vw] opacity-[1] pointer-events-none'>
        <ParallaxSection parallaxMultiplier={0.5}>
          {/*<img width='906' height='1000' className='h-[120vh] object-cover object-top' src='/images/constructionSite2.svg' alt='line drawing of a construction site' />*/}
          <img width='1536' height='1024' className='w-[100vw] min-h-[100vh] object-cover object-top' src='/images/constructionSite3.jpg' alt='a construction site' />
          {/* gradient at bottom to fade image into gray background */}
          <div className='absolute bottom-0 left-0 h-[15vh] w-[100vw] bg-gradient-to-t from-[#151515] to-[#15151500]'></div>
        </ParallaxSection>
      </div>
      {/* top right floating logo */}
      <div className='fixed top-[4em] left-[100%] translate-x-[-90%] opacity-[0.10] pointer-events-none'>
        <div className='sm:hidden'><Logo size={250} /></div>
        <div className='hidden sm:block 2xl:hidden'><Logo size={300} /></div>
        <div className='hidden 2xl:block'><Logo size={400} /></div>
      </div>
    </div>
  );
}

const mapStateToProps = state => { return { // define redux state that will be passed in as props
  _unsubscribeCode: state.localEphemeral.unsubscribeSubscriptionContactLinkCode,
  _unsubscribeEmail: state.localEphemeral.unsubscribeSubscriptionContactLinkEmail,
}};
export default connect(mapStateToProps, {setAlert, setLoading, siteboardAction})(SubscriptionContactUnsubscribePage);
